import React, { useEffect, useMemo, useState } from 'react'
import { getMessage } from 'lib/translator'
import AuthenticatedPage from 'containers/AuthenticatedPage'
import { Textarea } from 'components/Form'
import { Dialog } from 'components/Popup'
import Loader from 'components/Loader'
import API from 'lib/api'

import './style.scss'

const SKU_CONFIG_URL = '/offer-service/configurations/sku'

const parseSkuInput = (raw) =>
  raw
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean)

function SkuExclusions({ menu }) {
  const [textareaValue, setTextareaValue] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorDialog, setErrorDialog] = useState(null)
  const [successDialog, setSuccessDialog] = useState(null)

  useEffect(() => {
    const api = new API({ url: SKU_CONFIG_URL })
    api
      .get()
      .then((response) => {
        const skuList = Array.isArray(response?.data?.data)
          ? response.data.data
          : []
        setTextareaValue(skuList.join('\n'))
      })
      .catch(() => {
        setErrorDialog({
          title: 'Server Error',
          message: 'Unable to load SKU exclusions.',
        })
      })
      .finally(() => {
        setIsLoading(false)
      })

    return () => api?.cancel?.()
  }, [])

  const parsedSkuList = useMemo(
    () => parseSkuInput(textareaValue).map((value) => Number(value)),
    [textareaValue]
  )

  const hasInvalidSku = parsedSkuList.some((value) => Number.isNaN(value))

  const handleSubmit = () => {
    if (hasInvalidSku) {
      setErrorDialog({
        title: 'Validation Error',
        message: 'Only numeric SKU codes are allowed.',
      })
      return
    }

    setIsSubmitting(true)
    const api = new API({ url: SKU_CONFIG_URL })
    api
      .post({data: parsedSkuList})
      .then(() => {
        setSuccessDialog({
          title: 'Success',
          message: 'SKU exclusions updated successfully.',
        })
      })
      .catch(() => {
        setErrorDialog({
          title: 'Server Error',
          message: 'Unable to save SKU exclusions.',
        })
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <AuthenticatedPage className="sku-exclusions-page" menu={menu}>
        <div className="header-container">
            <h2>{getMessage('offer.skuExclusions.header')}</h2>
        </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="sku-exclusions-page__form">
					<Textarea
            className="sku-exclusions-textarea"
            type="text"
            name="sku-exclusions"
            testid="sku-exclusions"
            label={"List of SKUs to be excluded"}
            placeholder={'SKU_1\nSKU_2'}
            value={textareaValue}
						onChange={(value) => setTextareaValue(value)}
					/>

          <button
            type="button"
            className="button primary"
            onClick={handleSubmit}
            disabled={isSubmitting || hasInvalidSku}
          >
            {isSubmitting ? 'Saving...' : 'Submit'}
          </button>
        </div>
      )}

      {errorDialog && (
        <Dialog
          show
          title={errorDialog.title}
          information={errorDialog.message}
          close={() => setErrorDialog(null)}
          closeText="OK"
        />
      )}

      {successDialog && (
        <Dialog
          className="success"
          show
          title={successDialog.title}
          information={successDialog.message}
          close={() => setSuccessDialog(null)}
          closeText="OK"
        />
      )}
    </AuthenticatedPage>
  )
}

export default SkuExclusions

